/*
 * Copyright (c) 2020 Staffino, s.r.o. - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Created by Martin Komara, September 2020
 */

import { EventEmitter } from 'events';
import deepmerge from 'deepmerge';
import {
  FlowBlock,
  ConditionalStatement,
  FlowBlockRoot,
  PageState,
  FlowState,
  FlowBlockPage,
  FlowBlockTerminalPage,
  FlowBlockWarningPage,
} from '@staffino/model';
import FlowDefinitionTraverser from './flow-definition-traverser';
import { IFlowBridge } from './flow-bridge';
import extractValidConditionStatements from './extract-valid-condition-statements';
import { IEnvironmentBridge } from './environment-bridge';

const arrayMerge = (destinationArray, sourceArray) => sourceArray;

export default class FlowStateManager extends EventEmitter {
  private definitionTraverser: FlowDefinitionTraverser;

  private committed = false;

  private transientState: PageState = {};

  private transientStateStack: PageState[] = [];

  private transientOverrides: Record<string, any> = {};

  private transientOverridesStack: Array<Record<string, any>> = [];

  private attributeOverrides: Record<string, string> = {};

  private labelOverride: string;

  private currentPageIndex = 0;

  private statementCache: Map<FlowBlock, ConditionalStatement[]> = new Map();

  private lockedTransientOverrides: Record<string, any>;

  private lockCount = 0;

  constructor(
    definition: FlowBlockRoot,
    initialStateStack: FlowState,
    private readonly bridge: IFlowBridge,
    private readonly environmentBridge: IEnvironmentBridge,
  ) {
    super();
    this.definitionTraverser = new FlowDefinitionTraverser(definition);
    this.currentPageIndex = initialStateStack?.currentPageIndex ?? 0;
    this.transientStateStack = initialStateStack?.pageStates || [
      {
        formData: {},
        pageId: this.definitionTraverser.getPageAt(this.currentPageIndex)?.id,
      },
    ];

    // set last item as current
    this.transientState =
      this.transientStateStack[this.transientStateStack.length - 1];

    if (!this.transientState.targetId) {
      // so it is compatible with already stored flow sessions
      this.transientState.targetId = initialStateStack?.targetId;
    }

    this.transientOverridesStack = initialStateStack?.overridesStack || [];
    this.transientOverrides =
      this.transientOverridesStack[this.currentPageIndex] || [];
  }

  goToPage(index: number): Record<string, string> {
    // true if we are moving forward, false if backward
    const forward = index > this.currentPageIndex;
    const nextPage = this.getNextPage();

    const state = this.getValidState();
    this.transientStateStack[this.currentPageIndex] = { ...state };

    // if we are going forward, push transient overrides to the stack
    // if backward, clear overrides on previous page
    if (forward) {
      this.transientOverridesStack[this.currentPageIndex] = deepmerge(
        {
          ...this.getOverrides(),
          ...this.transientOverridesStack[this.currentPageIndex],
        },
        this.transientOverrides,
      );
    } else {
      this.transientOverridesStack[this.currentPageIndex - 1] = {};
    }

    this.currentPageIndex = index;
    this.transientStateStack[this.currentPageIndex] = this.transientStateStack[
      this.currentPageIndex
    ] ?? {
      formData: {},
      pageId: this.definitionTraverser.getPageAt(this.currentPageIndex)?.id,
    };

    this.resetTransientState();
    this.transientState = this.transientStateStack[this.currentPageIndex];

    // return null if next page does not exist
    if (!this.definitionTraverser.getPageAt(index)) {
      this.emitUpdateIfUnlocked();
      return null;
    }

    // skip page:
    // 1. if it is hidden or
    // 2. if next page to be displayed shouldn't be the one that succeeds
    if (
      this.getOverriddenBlock(this.getActivePage())?.hidden ||
      (nextPage &&
        this.currentPageIndex !==
          this.definitionTraverser.getPageIndex(nextPage))
    ) {
      this.transientStateStack[this.currentPageIndex].active = false;
      return this.goToPage(forward ? index + 1 : index - 1);
    }
    this.transientStateStack[this.currentPageIndex].active = true;
    this.emitUpdateIfUnlocked();
    return this.getTransientState().formData;
  }

  restartFlow() {
    this.tryCommit(true).then(() => {
      console.debug('[Flow.Restart] Commit successful. Reloading.');
      this.environmentBridge.restartFlow();
    });
  }

  finishFlow() {
    this.tryCommit(true).then(() => {
      console.debug('Finished flow. Reloading');
      this.environmentBridge.finishFlow()
    });
  }

  getOverrides() {
    return deepmerge(
      this.transientOverridesStack[this.currentPageIndex - 1],
      this.isLocked() ? this.lockedTransientOverrides : this.transientOverrides,
      { arrayMerge },
    );
  }

  getTransientOverrides() {
    return this.transientOverrides;
  }

  advancePage(): Record<string, string> {
    return this.goToPage(this.currentPageIndex + 1);
  }

  previousPage(): Record<string, string> {
    return this.goToPage(this.currentPageIndex - 1);
  }

  updateFormData(formData: Record<string, any>) {
    this.updateFormDataSilently(formData);
    this.emitUpdateIfUnlocked();
  }

  updateFormDataSilently(formData: Record<string, any>) {
    this.transientState.formData = formData;
  }

  getActivePage(): FlowBlockPage {
    const { pageId } = this.getState();
    return pageId && this.definitionTraverser.getPageById(pageId);
  }

  getNextPage(): FlowBlockPage {
    const { nextPageId } = this.getState();
    if (nextPageId) {
      return this.definitionTraverser.getPageById(nextPageId);
    }
    return null;
  }

  isLastPage(): boolean {
    const activePage = this.getActivePage();
    if (activePage) {
      let succeedingPage =
        this.definitionTraverser.getSucceedingPage(activePage);
      while (succeedingPage && this.getOverriddenBlock(succeedingPage).hidden) {
        // if next page is hidden, go ahead
        succeedingPage =
          this.definitionTraverser.getSucceedingPage(succeedingPage);
      }
      return !succeedingPage;
    }
    return false;
  }

  isFirstPage(): boolean {
    const activePage = this.getActivePage();
    if (activePage) {
      let previousPage = this.definitionTraverser.getPreviousPage(activePage);
      while (previousPage && this.getOverriddenBlock(previousPage).hidden) {
        // if previous page is hidden, go ahead
        previousPage = this.definitionTraverser.getSucceedingPage(previousPage);
      }
      return !previousPage;
    }
    return false;
  }

  getTerminalPage(): FlowBlockTerminalPage {
    return this.definitionTraverser.getTerminalPage();
  }

  getWarningPage(mainText?: string, text?: string): FlowBlockWarningPage {
    return (
      this.definitionTraverser.getWarningPage() ?? {
        type: 'warning-page',
        id: 'warning-page',
        children: [{ mainText, text, type: 'warning', id: 'warning' }],
      }
    );
  }

  resetTransientState() {
    this.transientState = { ...this.transientState, formData: {} };
    this.transientOverrides = {};
  }

  setNextPageId(id: string) {
    this.transientState.nextPageId = id;
  }

  setTargetId(targetId: string) {
    this.transientState.targetId = targetId;
  }

  getTargetId(): string {
    const stack = [
      ...this.transientStateStack.slice(0, this.currentPageIndex),
      this.getTransientState(),
    ];
    for (let i = stack.length - 1; i >= 0; i -= 1) {
      if (stack[i].active && stack[i].targetId) {
        return stack[i].targetId;
      }
    }
    return null;
  }

  getTransientState(): Partial<PageState> {
    return this.transientState;
  }

  getState(): PageState {
    return this.getTransientState();
  }

  getAggregatedFormData(): Record<string, any> {
    let formData = {};
    this.transientStateStack.forEach((item) => {
      formData = { ...formData, ...item.formData };
    });
    return deepmerge(formData, this.transientState.formData, {
      arrayMerge,
    });
  }

  getValidState(): PageState {
    const state = this.getState();
    const page =
      this.getActivePage() ?? this.definitionTraverser.getTerminalPage();
    const children = 'children' in page ? (page.children as FlowBlock[]) : [];
    // const blockMap = new Map<string, FlowBlock>(
    //   blocks.children.map((block) => [block.id, block])
    // );
    Object.keys(state.formData).forEach((key) => {
      // const block = blockMap.get(key);
      const block = children.find((b) => b.id === key);
      if (block?.type === 'question') {
        const value = state.formData[key];
        const score =
          value?.score === undefined || value.score === ''
            ? undefined
            : parseInt(value.score, 10);
        const comment =
          value?.comment === undefined || value.comment === ''
            ? undefined
            : value.comment;
        let choiceIds = value?.choiceIds?.filter((ch) => ch !== false);
        choiceIds =
          Array.isArray(choiceIds) &&
          choiceIds.length > 0 &&
          // single choice is weird if not selected
          !(choiceIds.length === 1 && choiceIds[0] === '')
            ? choiceIds
            : undefined;
        if (
          score !== undefined ||
          comment !== undefined ||
          choiceIds !== undefined
        ) {
          state.formData[key] = {};
          if (score !== undefined) {
            state.formData[key].score = score;
          }
          if (comment !== undefined) {
            state.formData[key].comment = comment;
          }
          if (choiceIds !== undefined) {
            state.formData[key].choiceIds = choiceIds;
          }
        } else {
          delete state.formData[key];
        }
      }
    });
    return state;
  }

  setOverride(blockId: string, properties: Record<string, any>) {
    this.transientOverrides[blockId] = {
      ...(this.transientOverrides[blockId] ?? {}),
      ...properties,
    };
    this.emitUpdateIfUnlocked();
  }

  getOverriddenBlock(block: Record<string, any>): Record<string, any> {
    return { ...block, ...(this.getOverrides()[block.id] ?? {}) };
  }

  setAttributeOverride(attribute: string, value: string) {
    this.attributeOverrides[attribute] = value;
    this.emitUpdateIfUnlocked();
  }

  setLabelOverride(label: string) {
    this.labelOverride = label;
    this.emitUpdateIfUnlocked();
  }

  getActiveStatements(): ConditionalStatement[] {
    const page = this.getActivePage() ?? this.getTerminalPage();
    if (this.statementCache.has(page)) {
      return this.statementCache.get(page);
    }
    const validStatements = this.getStatementsForPage(page);
    this.statementCache.set(page, validStatements);
    return validStatements;
  }

  getStatementsForPage(page: FlowBlockPage | FlowBlockTerminalPage) {
    const prevPage =
      page !== this.getTerminalPage()
        ? this.definitionTraverser.getPreviousPage(page as FlowBlockPage)
        : this.definitionTraverser.getLastPage();
    if (prevPage) {
      return [
        ...this.getStatementsForPage(prevPage),
        ...extractValidConditionStatements(page),
      ];
    }
    return extractValidConditionStatements(page);
  }

  async commit(autoCommitted = false): Promise<void> {
    // mark as committed before we actually commit so that we don't wait for
    // network request here
    this.committed = true;
    try {
      await this.bridge.commitState(this.getFlowState(), { autoCommitted });
    } catch (e) {
      this.committed = false;
      throw e;
    }
  }

  async tryCommit(autoCommitted = false): Promise<void> {
    if (!this.committed) {
      await this.commit(autoCommitted);
    }
  }

  private getFlowState(): FlowState {
    const stack = [
      ...this.transientStateStack.slice(0, this.currentPageIndex),
      this.getTransientState(),
    ];

    return {
      currentPageIndex: this.currentPageIndex,
      pageStates: stack,
      overridesStack: this.transientOverridesStack,
      attributeOverrides: this.attributeOverrides,
      labelOverride: this.labelOverride,
      targetId: this.getTargetId(),
    };
  }

  async updateFlowSession(): Promise<void> {
    await this.bridge.updateFlowSession(this.getFlowState());
  }

  getCurrentPageIndex() {
    return this.currentPageIndex;
  }

  reevaluateConditions(formValues, evaluator) {
    this.lock();
    this.resetTransientState();
    this.updateFormDataSilently(formValues);
    const statements = this.getActiveStatements();
    return evaluator
      .evaluateStatements(statements)
      .finally(() => this.unlock());
  }

  private lock() {
    if (this.lockCount === 0) {
      this.lockedTransientOverrides = this.transientOverrides;
    }
    this.lockCount += 1;
  }

  private unlock() {
    if (this.lockCount > 0) {
      this.lockCount -= 1;
      this.emitUpdateIfUnlocked();
    }
  }

  private isLocked() {
    return this.lockCount > 0;
  }

  private emitUpdateIfUnlocked() {
    if (!this.isLocked()) {
      this.emit('update');
    }
  }
}
